import { z } from 'zod';

import type { TFunction } from 'i18next';

const addressFormSchema = z.object({
  postcode: z.string(),
  city: z.string(),
  street: z.string(),
  building: z.string(),
  office: z.string(),
});

export const INVENTORY_SUPPLIER_ADDRESS_PARTS =
  addressFormSchema.keyof().options;

const taxFormSchema = z.object({
  registration_code: z.string(),
  vat_payer: z.boolean(),
  vat_number: z.string(),
});

const bankFormSchema = z.object({
  iban: z.string(),
  bank_name: z.string(),
});

const optionalEmailSchema = (t: TFunction) =>
  z
    .string()
    .email({ message: t('validation_email') })
    .or(z.literal(''));

const contactFormSchema = (t: TFunction) =>
  z
    .object({
      id: z.number().optional(),
      last_name: z.string(),
      first_name: z.string(),
      middle_name: z.string(),
      job_title: z.string(),
      phone: z.string(),
      email: optionalEmailSchema(t),
      notes: z.string(),
    })
    .refine(
      (contact) =>
        contact.first_name.trim() !== '' || contact.last_name.trim() !== '',
      {
        path: ['first_name'],
        message: t('Contact name required'),
      },
    );

export function createInventorySupplierFormSchema(t: TFunction) {
  const required = { message: t('validation_required') };

  return z
    .object({
      legal_entity_type: z.string().nullable(),
      name: z.string(required).trim().min(1, required),
      phone: z.string(),
      email: optionalEmailSchema(t),
      website: z.string(),
      notes: z.string(),
      contacts: z.array(contactFormSchema(t)),
      legal_address: addressFormSchema,
      actual_address_different: z.boolean(),
      actual_address: addressFormSchema,
      ...taxFormSchema.shape,
      ...bankFormSchema.shape,
      opening_balance: z.string().refine((value) => !isNaN(Number(value)), {
        message: t('validation_invalid'),
      }),
    })
    .superRefine((supplier, ctx) => {
      if (supplier.vat_payer && supplier.vat_number.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          path: ['vat_number'],
          message: t('validation_required'),
        });
      }
    });
}

export type InventorySupplierAddressKind = 'legal' | 'actual';

export type InventorySupplierAddressFormValues = z.infer<
  typeof addressFormSchema
>;

export type InventorySupplierFormValues = z.infer<
  ReturnType<typeof createInventorySupplierFormSchema>
>;

export type InventorySupplierContactFormValues =
  InventorySupplierFormValues['contacts'][number];
